/**
 * ClickableMetric Component
 *
 * A wrapper that makes any metric value clickable to show evidence.
 * Displays a subtle indicator that the metric is explainable.
 *
 * Usage:
 *   <ClickableMetric uiFieldPath="current_finances.gross_income" runId={runId}>
 *     <div className="text-2xl">{formatCurrency(income)}</div>
 *   </ClickableMetric>
 *
 * BUG-UI-061: Added incomeBasis support for gross_income_6m comparison
 */

import React, { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { useEvidenceModal, IncomeBasisData, DashboardValueData } from '@/context/EvidenceContext';
import { DisclosureLevel } from '@/types/evidenceContract';
import { getEvidenceConfig } from '@/config/uiEvidenceMap';

interface ClickableMetricProps {
  /** UI field path that maps to the evidence config (e.g., 'current_finances.gross_income') */
  uiFieldPath: string;
  /** The run_id from algo_runs table for this member's most recent calculation */
  runId: string;
  /** BUG-UI-092: Member ID for cross-run historical evidence */
  memberId?: string;
  /** Override the default disclosure level (defaults to LENDER) */
  disclosureLevel?: DisclosureLevel;
  /** Optional member name for display in modal */
  memberName?: string;
  /** The metric content to render */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the info icon indicator (default: true) */
  showIndicator?: boolean;
  /** Disable the click behavior (e.g., when no runId available) */
  disabled?: boolean;
  /** BUG-UI-061: Income basis data for gross_income_6m comparison */
  incomeBasis?: IncomeBasisData;
  /** BUG-UI-120: Dashboard value for reconciliation with modal display */
  dashboardValue?: DashboardValueData;
  /** Option 3: Initial month to display in modal (from card's selected month) */
  initialMonth?: string | null;
  /** BUG-UI-128: Custom tooltip text (default: "Click to see how this was calculated") */
  tooltipText?: string;
}

export function ClickableMetric({
  uiFieldPath,
  runId,
  memberId,
  disclosureLevel = 'LENDER',
  memberName,
  children,
  className = '',
  showIndicator = true,
  disabled = false,
  incomeBasis,
  dashboardValue,
  initialMonth,
  tooltipText = 'Click to see how this was calculated',
}: ClickableMetricProps) {
  const { showEvidence } = useEvidenceModal();

  // Check if this field has evidence configuration
  const evidenceConfig = getEvidenceConfig(uiFieldPath);
  const hasEvidence = !!evidenceConfig && !!runId && !disabled;

  // DEBUG: Log click eligibility
  console.log(`🔍 [ClickableMetric] ${uiFieldPath}:`, {
    hasConfig: !!evidenceConfig,
    runId: runId || 'EMPTY',
    disabled,
    hasEvidence,
  });

  const handleClick = () => {
    console.log(`🖱️ [ClickableMetric] Click on ${uiFieldPath}, hasEvidence=${hasEvidence}, initialMonth=${initialMonth}`);
    if (!hasEvidence) return;

    // BUG-UI-061: Pass incomeBasis for gross_income_6m comparison
    // BUG-UI-092: Pass memberId for cross-run historical evidence
    // BUG-UI-120: Pass dashboardValue for reconciliation
    // Option 3: Pass initialMonth so modal opens at selected month
    console.log(`🖱️ [ClickableMetric] Click with initialMonth="${initialMonth}" for ${uiFieldPath}`);
    showEvidence({
      uiFieldPath,
      runId,
      memberId,
      disclosureLevel,
      memberName,
      incomeBasis,
      dashboardValue,
      initialMonth,
    });
  };

  // If no evidence available, just render children without click behavior
  if (!hasEvidence) {
    return <>{children}</>;
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(`🔘 [ClickableMetric] Button clicked - ${uiFieldPath}`);
        handleClick();
      }}
      onMouseDown={(e) => {
        console.log(`⬇️ [ClickableMetric] MouseDown - ${uiFieldPath}`);
      }}
      className={`
        group relative inline-flex items-center gap-1.5
        cursor-pointer hover:opacity-80 transition-opacity
        text-left
        ${className}
      `}
      style={{ pointerEvents: 'auto' }}
      title={tooltipText}
      aria-label={`View evidence for ${evidenceConfig?.label || uiFieldPath}`}
    >
      {children}
      {showIndicator && (
        <span className="inline-flex opacity-0 group-hover:opacity-100 transition-opacity">
          <Info className="w-3.5 h-3.5 text-blue-500" />
        </span>
      )}
    </button>
  );
}

/**
 * Badge variant that shows a persistent "Explain" indicator
 */
export function ExplainableBadge({
  uiFieldPath,
  runId,
  disclosureLevel = 'LENDER',
  memberName,
  children,
  className = '',
}: Omit<ClickableMetricProps, 'showIndicator' | 'disabled'>) {
  const { showEvidence } = useEvidenceModal();
  const evidenceConfig = getEvidenceConfig(uiFieldPath);
  const hasEvidence = !!evidenceConfig && !!runId;

  const handleClick = () => {
    if (!hasEvidence) return;
    showEvidence({ uiFieldPath, runId, disclosureLevel, memberName });
  };

  if (!hasEvidence) {
    return <>{children}</>;
  }

  return (
    <div className={`relative ${className}`}>
      {children}
      <button
        onClick={handleClick}
        className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
        title="Click to see evidence"
      >
        ?
      </button>
    </div>
  );
}

